// Breadcrumb trail for a path, shared by the visible BreadCrumb and the
// BreadcrumbList JSON-LD so the markup always names and links exactly what the
// page shows.

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

// "15-10-2026" → "15 October 2026"
const formatDateSlug = (slug: string): string | null => {
    const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(slug);
    if (!match) return null;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${day} ${MONTH_NAMES[month - 1]} ${year}`;
};

const toCapitalCase = (str: string) =>
    str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
        .replace(/@/g, ' at ')
        .replace(/_/g, ' ');

const crumbName = (page: string) => {
    if (page === '') return 'Home';
    if (page === 'articles') return 'Sacred Threads';
    // The language suffix of a rashifal page (/rashifal/libra/today/hi).
    if (page === 'hi') return 'हिन्दी';
    const date = formatDateSlug(page);
    if (date) return date;
    // "2026-11" (a festival's month page) → "November 2026"
    const month = /^(\d{4})-(\d{2})$/.exec(page);
    if (month && Number(month[2]) >= 1 && Number(month[2]) <= 12) return `${MONTH_NAMES[Number(month[2]) - 1]} ${month[1]}`;
    return toCapitalCase(
        page
            .replace(/-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/, '')
            .replace(/-/g, ' ')
            .replace(/_/g, ' ')
    )
        .replaceAll('-', ' ')
        .replaceAll('%20', '-')
        .replaceAll('Deities', 'Devta');
};

// Some intermediate paths only redirect; link the crumb to where they land.
// /rashifal/<sign> → /rashifal/<sign>/today
const crumbHref = (segments: string[]) => {
    const path = `/${segments.join('/')}`;
    if (segments.length === 2 && segments[0] === 'rashifal' && segments[1] !== 'today') return `${path}/today`;
    return path;
};

// Regional festival pages emit their own, in their language.
export const ownsBreadcrumbJsonLd = (pathname: string) => /^\/(hi|mr|kn)\/festivals\//.test(pathname);

export const breadcrumbTrail = (pathname: string): { name: string; href: string }[] => {
    const pages = pathname.split('/').filter(Boolean);
    // Hindi articles: there is no /hi page, so the trail is Home › हिंदी लेख
    // (/hi/articles) › the article.
    if (pages[0] === 'hi' && pages[1] === 'articles') {
        return [
            { name: crumbName(''), href: '/' },
            { name: 'हिंदी लेख', href: '/hi/articles' },
            ...pages.slice(2, 3).map((page) => ({ name: crumbName(page), href: `/${pages.slice(0, 3).join('/')}` })),
        ];
    }
    return [''].concat(pages).map((page, index) => ({
        name: crumbName(page),
        href: index > 0 ? crumbHref(pages.slice(0, index)) : '/',
    }));
};
